import type { RolUsuario } from '../../types';
/**
 * EditarMedicoModal — Edita un médico (registrado o pre-registrado).
 *
 * Modo determinado por `tipo`:
 *   - 'medico'      → llama portal_actualizarMedico
 *   - 'preregistro' → llama portal_actualizarMedicoPreRegistro
 *
 * Campos editables: nombre, apellidos, teléfono, cédula, No. de Registro de
 * Idoneidad, especialidad, subespecialidad, centroMedico, códigos SEIS,
 * role, salasAsignadas.
 *
 * Campos READ-ONLY: email (cambiarlo requiere flujo de re-verificación).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CheckCircle2, UserCog, Camera, Trash2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Avatar } from '../ui/Avatar';
import { functions } from '../../config/firebase';
import { useCollection } from '../../hooks/useFirestore';
import { useToast } from '../../contexts/ToastContext';
import { uploadMedicoAvatarAdmin, deleteMedicoAvatarAdmin } from '../../services/profileService';
import { MAX_30, soloAlfanumericoGuiones, soloDigitos } from '../../utils/inputFilters';
import styles from './PreRegistroFormModal.module.css';

const ESPECIALIDADES = [
  'Medicina General',
  'Pediatría',
  'Ginecología',
  'Cardiología',
  'Dermatología',
  'Psicología',
  'Psiquiatría',
  'Traumatología',
  'Neurología',
  'Urología',
  'Oftalmología',
  'Otorrinolaringología',
  'Otro',
];

const callActualizarMedico = httpsCallable(functions, 'portal_actualizarMedico');
const callActualizarPreRegistro = httpsCallable(
  functions,
  'portal_actualizarMedicoPreRegistro',
);

export interface EditarMedicoModalProps {
  open: boolean;
  onClose: () => void;
  tipo: 'medico' | 'preregistro';
  /** Documento Firestore actual del médico o pre-registro. */
  target: {
    id: string;
    email?: string;
    /** Cédula del médico. */
    cedula?: string | null;
    /** No. de Registro de Idoneidad. */
    colegiadoId?: string;
    nombre?: string;
    apellidos?: string;
    telefono?: string;
    especialidad?: string;
    subespecialidad?: string | null;
    centroMedico?: string;
    role?: RolUsuario;
    salasAsignadas?: string[];
    /** Código del médico en SEIS. */
    seisMedicoId?: string | null;
    /** Código de la especialidad en SEIS. */
    seisEspecialidadId?: string | null;
    /** Código de la instalación de salud en SEIS. */
    seisInstalacionId?: string | null;
    /** Foto de perfil actual del médico (medicos/{uid}.avatarUrl). */
    avatarUrl?: string | null;
    /** M8: skills del médico */
    skills?: {
      idiomas?: string[];
      edadesAtendidas?: { min: number; max: number };
      subEspecialidades?: string[];
      nivelUrgenciaMax?: number;
    };
  };
}

const IDIOMAS_DISPONIBLES = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'Inglés' },
  { code: 'ngäbe', label: 'Ngäbe-buglé' },
  { code: 'kuna', label: 'Kuna' },
  { code: 'embera', label: 'Emberá' },
];

export function EditarMedicoModal({
  open,
  onClose,
  tipo,
  target,
}: EditarMedicoModalProps) {
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  // ── Foto de perfil del médico (solo médicos activados) ──
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onSelectFoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-seleccionar el mismo archivo
    if (!file) return;
    setSubiendoFoto(true);
    try {
      const url = await uploadMedicoAvatarAdmin(target.id, file);
      setAvatarUrl(url);
      addToast('success', 'Foto actualizada');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'No se pudo subir la foto');
    } finally {
      setSubiendoFoto(false);
    }
  };

  const onQuitarFoto = async () => {
    setSubiendoFoto(true);
    try {
      await deleteMedicoAvatarAdmin(target.id);
      setAvatarUrl(null);
      addToast('success', 'Foto eliminada');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'No se pudo eliminar la foto');
    } finally {
      setSubiendoFoto(false);
    }
  };

  const [form, setForm] = useState({
    nombre: '',
    apellidos: '',
    telefono: '',
    cedula: '',
    colegiadoId: '',
    especialidad: '',
    subespecialidad: '',
    centroMedico: '',
    seisMedicoId: '',
    seisEspecialidadId: '',
    seisInstalacionId: '',
    role: 'medico' as 'medico' | 'supervisor',
    salasAsignadas: [] as string[],
    // M8 skills
    idiomas: [] as string[],
    edadMin: 0,
    edadMax: 120,
    subEspecialidades: [] as string[],
  });

  const [showSkills, setShowSkills] = useState(false);

  // Hidratar form cuando se abre con un target
  useEffect(() => {
    if (!open) return;
    setAvatarUrl(target.avatarUrl ?? null);
    setForm({
      nombre: target.nombre ?? '',
      apellidos: target.apellidos ?? '',
      telefono: target.telefono ?? '',
      cedula: target.cedula ?? '',
      colegiadoId: target.colegiadoId ?? '',
      especialidad: target.especialidad ?? '',
      subespecialidad: target.subespecialidad ?? '',
      centroMedico: target.centroMedico ?? '',
      seisMedicoId: target.seisMedicoId ?? '',
      seisEspecialidadId: target.seisEspecialidadId ?? '',
      seisInstalacionId: target.seisInstalacionId ?? '',
      role: (target.role === 'supervisor' ? 'supervisor' : 'medico') as
        | 'medico'
        | 'supervisor',
      salasAsignadas: target.salasAsignadas ?? [],
      idiomas: target.skills?.idiomas ?? [],
      edadMin: target.skills?.edadesAtendidas?.min ?? 0,
      edadMax: target.skills?.edadesAtendidas?.max ?? 120,
      subEspecialidades: target.skills?.subEspecialidades ?? [],
    });
  }, [open, target]);

  const toggleIdioma = (code: string) =>
    setForm((f) => ({
      ...f,
      idiomas: f.idiomas.includes(code)
        ? f.idiomas.filter((i) => i !== code)
        : [...f.idiomas, code],
    }));

  // Salas disponibles para multi-select
  const { data: salasRaw } = useCollection<{ id: string; nombre: string }>(
    'salas',
    [where('activa', '==', true)],
  );
  const salas = salasRaw || [];

  const updateField = (field: string, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toggleSala = (salaId: string) =>
    setForm((f) => ({
      ...f,
      salasAsignadas: f.salasAsignadas.includes(salaId)
        ? f.salasAsignadas.filter((s) => s !== salaId)
        : [...f.salasAsignadas, salaId],
    }));

  // Nota: la cédula NO se exige aquí (a diferencia del pre-registro) porque
  // es un campo nuevo — los médicos existentes aún no la tienen, y exigirla
  // bloquearía ediciones no relacionadas (p. ej. corregir el teléfono) hasta
  // completar el backfill. El admin puede capturarla cuando la tenga a mano.
  const canSubmit =
    form.nombre.trim() !== '' &&
    form.apellidos.trim() !== '' &&
    form.colegiadoId.trim() !== '' &&
    form.especialidad !== '' &&
    form.centroMedico.trim() !== '' &&
    !submitting;

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Only send fields that actually changed from the original target values
      const payload: Record<string, unknown> = {};

      const trimmed = {
        nombre: form.nombre.trim(),
        apellidos: form.apellidos.trim(),
        telefono: form.telefono.trim(),
        cedula: form.cedula.trim(),
        colegiadoId: form.colegiadoId.trim(),
        especialidad: form.especialidad,
        subespecialidad: form.subespecialidad.trim() || null,
        centroMedico: form.centroMedico.trim(),
        seisMedicoId: form.seisMedicoId.trim() || null,
        seisEspecialidadId: form.seisEspecialidadId.trim() || null,
        seisInstalacionId: form.seisInstalacionId.trim() || null,
        role: form.role,
        salasAsignadas: form.salasAsignadas,
      };

      if (trimmed.nombre !== (target.nombre ?? '')) payload.nombre = trimmed.nombre;
      if (trimmed.apellidos !== (target.apellidos ?? '')) payload.apellidos = trimmed.apellidos;
      if (trimmed.telefono !== (target.telefono ?? '')) payload.telefono = trimmed.telefono;
      if (trimmed.cedula !== (target.cedula ?? '')) payload.cedula = trimmed.cedula;
      if (trimmed.colegiadoId !== (target.colegiadoId ?? '')) payload.colegiadoId = trimmed.colegiadoId;
      if (trimmed.especialidad !== (target.especialidad ?? '')) payload.especialidad = trimmed.especialidad;
      if (trimmed.subespecialidad !== (target.subespecialidad ?? '')) payload.subespecialidad = trimmed.subespecialidad;
      if (trimmed.centroMedico !== (target.centroMedico ?? '')) payload.centroMedico = trimmed.centroMedico;
      if ((trimmed.seisMedicoId ?? null) !== (target.seisMedicoId ?? null)) payload.seisMedicoId = trimmed.seisMedicoId;
      if ((trimmed.seisEspecialidadId ?? null) !== (target.seisEspecialidadId ?? null)) payload.seisEspecialidadId = trimmed.seisEspecialidadId;
      if ((trimmed.seisInstalacionId ?? null) !== (target.seisInstalacionId ?? null)) payload.seisInstalacionId = trimmed.seisInstalacionId;
      if (trimmed.role !== (target.role === 'supervisor' ? 'supervisor' : 'medico')) payload.role = trimmed.role;

      const origSalas = target.salasAsignadas ?? [];
      if (
        trimmed.salasAsignadas.length !== origSalas.length ||
        trimmed.salasAsignadas.some((s, i) => s !== origSalas[i])
      ) {
        payload.salasAsignadas = trimmed.salasAsignadas;
      }

      // M8: agregar skills solo si fueron editados (showSkills abierto)
      if (showSkills) {
        payload.skills = {
          idiomas: form.idiomas,
          edadesAtendidas: { min: form.edadMin, max: form.edadMax },
          subEspecialidades: form.subEspecialidades,
        };
      }

      if (tipo === 'medico') {
        payload.medicoUid = target.id;
        if (Object.keys(payload).length <= 1) {
          addToast('info', 'No se detectaron cambios');
          setSubmitting(false);
          return;
        }
        await callActualizarMedico(payload);
        addToast('success', `Usuario ${form.nombre} ${form.apellidos} actualizado`);
      } else {
        payload.preRegistroId = target.id;
        if (Object.keys(payload).length <= 1) {
          addToast('info', 'No se detectaron cambios');
          setSubmitting(false);
          return;
        }
        await callActualizarPreRegistro(payload);
        addToast('success', `Pre-registro de ${form.nombre} actualizado`);
      }
      onClose();
    } catch (err) {
      console.error('[EditarMedicoModal]', err);
      const msg =
        err instanceof Error
          ? err.message
          : 'Error al guardar los cambios';
      addToast('error', msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={tipo === 'preregistro' ? 'Editar pre-registro' : 'Editar usuario'}
      size="lg"
    >
      <div className={styles.form}>
        <p className={styles.intro}>
          <UserCog size={14} aria-hidden="true" />
          Actualice los datos del usuario. El email es inmutable.
        </p>

        {/* Foto de perfil — solo médicos ya activados (no en pre-registro) */}
        {tipo === 'medico' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: '4px 0 12px',
            }}
          >
            <Avatar
              nombre={`${form.nombre} ${form.apellidos}`.trim() || target.nombre}
              fotoURL={avatarUrl}
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, var(--brand-green-700), var(--brand-green-900))',
                color: '#FFFEF5',
                fontWeight: 600,
                fontSize: 20,
                flexShrink: 0,
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={subiendoFoto}
                >
                  <Camera size={14} aria-hidden="true" />
                  {subiendoFoto ? 'Subiendo…' : avatarUrl ? 'Cambiar foto' : 'Subir foto'}
                </button>
                {avatarUrl && (
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={onQuitarFoto}
                    disabled={subiendoFoto}
                  >
                    <Trash2 size={14} aria-hidden="true" /> Eliminar
                  </button>
                )}
              </div>
              <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: 0 }}>
                JPG, PNG o WebP · máx 2 MB. Sin foto se muestran las iniciales.
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onSelectFoto}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {/* Read-only context */}
        <div className={styles.readOnlyRow}>
          <div className={styles.readOnlyField}>
            <label className={styles.label}>Email</label>
            <div className={styles.readOnlyValue}>{target.email}</div>
          </div>
        </div>

        <div className={styles.grid}>
          <Input
            label="Nombre *"
            value={form.nombre}
            onChange={(e) => updateField('nombre', e.target.value)}
            disabled={submitting}
            required
          />
          <Input
            label="Apellidos *"
            value={form.apellidos}
            onChange={(e) => updateField('apellidos', e.target.value)}
            disabled={submitting}
            required
          />
          <Input
            label="Teléfono"
            type="tel"
            placeholder="+507 6000-0000"
            value={form.telefono}
            onChange={(e) => updateField('telefono', e.target.value)}
            disabled={submitting}
          />
          <Input
            label="Cédula"
            placeholder="8-888-8888"
            maxLength={MAX_30}
            value={form.cedula}
            onChange={(e) => updateField('cedula', soloAlfanumericoGuiones(e.target.value))}
            disabled={submitting}
          />
          <Input
            label="No. de Registro de Idoneidad *"
            placeholder="MED-12345"
            maxLength={MAX_30}
            value={form.colegiadoId}
            onChange={(e) => updateField('colegiadoId', soloAlfanumericoGuiones(e.target.value))}
            disabled={submitting}
            required
          />
          <div className={styles.field}>
            <label className={styles.label}>Especialidad *</label>
            <select
              className={styles.select}
              value={form.especialidad}
              onChange={(e) => updateField('especialidad', e.target.value)}
              disabled={submitting}
              required
            >
              <option value="">Seleccione…</option>
              {ESPECIALIDADES.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Subespecialidad"
            placeholder="Opcional"
            maxLength={MAX_30}
            value={form.subespecialidad}
            onChange={(e) => updateField('subespecialidad', e.target.value.slice(0, MAX_30))}
            disabled={submitting}
          />
          <Input
            label="Centro Médico *"
            maxLength={MAX_30}
            value={form.centroMedico}
            onChange={(e) => updateField('centroMedico', e.target.value.slice(0, MAX_30))}
            disabled={submitting}
            required
          />
          <Input
            label="Código de médico en HCIS"
            placeholder="Ej. 407718"
            inputMode="numeric"
            maxLength={MAX_30}
            value={form.seisMedicoId}
            onChange={(e) => updateField('seisMedicoId', soloDigitos(e.target.value))}
            disabled={submitting}
          />
          <Input
            label="Código de especialidad en SEIS"
            placeholder="Ej. 4573"
            inputMode="numeric"
            maxLength={MAX_30}
            value={form.seisEspecialidadId}
            onChange={(e) => updateField('seisEspecialidadId', soloDigitos(e.target.value))}
            disabled={submitting}
          />
          <Input
            label="Código de instalación de salud en SEIS"
            placeholder="Ej. 136"
            inputMode="numeric"
            maxLength={MAX_30}
            value={form.seisInstalacionId}
            onChange={(e) => updateField('seisInstalacionId', soloDigitos(e.target.value))}
            disabled={submitting}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Rol *</label>
          <div className={styles.roleOptions}>
            <label className={styles.radio}>
              <input
                type="radio"
                name="role-edit"
                value="medico"
                checked={form.role === 'medico'}
                onChange={() => updateField('role', 'medico')}
                disabled={submitting}
              />
              <span>Médico</span>
            </label>
            <label className={styles.radio}>
              <input
                type="radio"
                name="role-edit"
                value="supervisor"
                checked={form.role === 'supervisor'}
                onChange={() => updateField('role', 'supervisor')}
                disabled={submitting}
              />
              <span>Supervisor</span>
            </label>
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            Salas asignadas ({form.salasAsignadas.length} seleccionadas)
          </label>
          <div className={styles.salasGrid}>
            {salas.map((sala) => (
              <label key={sala.id} className={styles.salaCheck}>
                <input
                  type="checkbox"
                  checked={form.salasAsignadas.includes(sala.id)}
                  onChange={() => toggleSala(sala.id)}
                  disabled={submitting}
                />
                <span>{sala.nombre}</span>
              </label>
            ))}
            {salas.length === 0 && (
              <p className={styles.noSalas}>No hay salas activas.</p>
            )}
          </div>
        </div>

        {/* ── M8: Skills (colapsable) ─────────────────────────── */}
        <div className={styles.field}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => setShowSkills((v) => !v)}
            style={{ alignSelf: 'flex-start' }}
          >
            {showSkills ? '▲ Ocultar' : '▼ Configurar'} skills avanzados (idiomas, edades)
          </button>

          {showSkills && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                background: 'var(--color-surface-subtle)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div>
                <label className={styles.label}>Idiomas</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                  {IDIOMAS_DISPONIBLES.map((i) => (
                    <label key={i.code} className={styles.salaCheck}>
                      <input
                        type="checkbox"
                        checked={form.idiomas.includes(i.code)}
                        onChange={() => toggleIdioma(i.code)}
                      />
                      <span>{i.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.grid}>
                <div className={styles.field}>
                  <label className={styles.label}>Edad mínima atendida</label>
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={form.edadMin}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        edadMin: Math.max(0, parseInt(e.target.value) || 0),
                      })
                    }
                    className={styles.select}
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Edad máxima atendida</label>
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={form.edadMax}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        edadMax: Math.min(120, parseInt(e.target.value) || 120),
                      })
                    }
                    className={styles.select}
                  />
                </div>
              </div>

              <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: 0 }}>
                Ej. pediatra: 0-14 · adulto: 15-120 · geriatra: 60-120
              </p>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => handleSubmit()}
            disabled={!canSubmit}
          >
            {submitting ? (
              'Guardando…'
            ) : (
              <>
                <CheckCircle2 size={16} aria-hidden="true" /> Guardar cambios
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
